import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import T2TTopNav from "../Layout/T2TTopNav";
import { getData, postData, putData, deleteData } from "../../../FetchApiServices";
import { useToast, useConfirm } from "../Layout/T2TUIProvider";

const TYPE_COLORS = {
  Company:    "#36B82A",
  Division:   "#2563EB",
  Department: "#D97706",
  Team:       "#7C3AED",
  Group:      "#0F766E",
};

const TYPE_OPTIONS = ["Company", "Division", "Department", "Team", "Group"];

// Build nested tree from flat list
const buildTree = (nodes, parentId = null) =>
  nodes
    .filter((n) => n.parent_id === parentId)
    .map((n) => ({ ...n, children: buildTree(nodes, n.id) }));

// Collect all descendant IDs recursively (used locally before API call)
const collectDescendants = (nodes, id) => {
  const children = nodes.filter((n) => n.parent_id === id);
  return children.reduce(
    (acc, c) => [...acc, c.id, ...collectDescendants(nodes, c.id)],
    []
  );
};

const TreeNode = ({ node, depth, selectedId, expandedIds, onSelect, onToggle }) => {
  const hasChildren = node.children?.length > 0;
  const isExpanded  = expandedIds.has(node.id);
  const color       = TYPE_COLORS[node.type] || "#94A3B8";
  const badge       = node.type.slice(0, 3).toUpperCase();

  return (
    <div>
      <div
        className={`t2t-org-tree-node${selectedId === node.id ? " selected" : ""}`}
        style={{ paddingLeft: 12 + depth * 18 }}
        onClick={() => onSelect(node.id)}
      >
        <span
          style={{ width: 16, height: 16, display: "inline-flex", alignItems: "center", justifyContent: "center", fontSize: 10, color: "var(--t2t-text-muted)", cursor: hasChildren ? "pointer" : "default", flexShrink: 0 }}
          onClick={(e) => { if (hasChildren) { e.stopPropagation(); onToggle(node.id); } }}
        >
          {hasChildren ? (isExpanded ? "▼" : "▶") : "•"}
        </span>
        <span className="t2t-org-type-badge" style={{ background: color }}>
          {badge}
        </span>
        <span className="t2t-org-node-name">{node.name}</span>
        <span style={{ fontSize: 10, color: "var(--t2t-text-muted)", fontFamily: "monospace", marginLeft: "auto" }}>{node.code}</span>
      </div>
      {isExpanded && node.children?.map((child) => (
        <TreeNode
          key={child.id}
          node={child}
          depth={depth + 1}
          selectedId={selectedId}
          expandedIds={expandedIds}
          onSelect={onSelect}
          onToggle={onToggle}
        />
      ))}
    </div>
  );
};

TreeNode.propTypes = {
  node:        PropTypes.object.isRequired,
  depth:       PropTypes.number.isRequired,
  selectedId:  PropTypes.number,
  expandedIds: PropTypes.instanceOf(Set).isRequired,
  onSelect:    PropTypes.func.isRequired,
  onToggle:    PropTypes.func.isRequired,
};

const T2TOrgSetup = () => {
  const toast   = useToast();
  const confirm = useConfirm();

  const [nodes, setNodes]             = useState([]);
  const [selectedId, setSelectedId]   = useState(null);
  const [expandedIds, setExpandedIds] = useState(new Set());
  const [editForm, setEditForm]       = useState({});
  const [loading, setLoading]         = useState(false);
  const [saving, setSaving]           = useState(false);
  const [error, setError]             = useState("");

  const selectedNode = nodes.find((n) => n.id === selectedId) || null;
  const tree         = buildTree(nodes);

  // ---- Fetch all org units on mount ----
  useEffect(() => {
    fetchOrgUnits();
  }, []);

  const fetchOrgUnits = async () => {
    setLoading(true);
    setError("");
    const resp = await getData("t2t/org-units");
    if (resp.error) {
      setError(resp.error.message || "Failed to load org units.");
    } else {
      const data = resp.data?.data || [];
      setNodes(data);
      // Auto-expand root nodes
      const rootIds = data.filter((n) => n.parent_id === null).map((n) => n.id);
      setExpandedIds(new Set(rootIds));
    }
    setLoading(false);
  };

  // ---- Toggle expand ----
  const toggleNode = (id) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  // ---- Select — load into form ----
  const handleSelect = (id) => {
    const node = nodes.find((n) => n.id === id);
    setSelectedId(id);
    setEditForm({ name: node.name, type: node.type, code: node.code, parent_id: node.parent_id ?? "" });
    setError("");
  };

  // ---- Form field change ----
  const handleFieldChange = (field, value) => {
    setEditForm((prev) => ({ ...prev, [field]: value }));
  };

  // ---- Save Changes ----
  const handleSave = async () => {
    if (!editForm.name?.trim()) { setError("Name is required."); return; }
    setSaving(true);
    setError("");
    const payload = {
      name:      editForm.name,
      type:      editForm.type,
      code:      editForm.code || "",
      parent_id: editForm.parent_id === "" ? null : Number(editForm.parent_id),
    };
    const resp = await putData(`t2t/org-units/${selectedId}`, payload);
    if (resp.error) {
      setError(resp.error.message || "Failed to save.");
    } else {
      await fetchOrgUnits();
      // Keep selection on the same node after refresh
      setSelectedId(selectedId);
      const updated = resp.data?.data;
      if (updated) {
        setEditForm({ name: updated.name, type: updated.type, code: updated.code, parent_id: updated.parent_id ?? "" });
      }
    }
    setSaving(false);
  };

  // ---- Add Root ----
  const addRoot = async () => {
    setSaving(true);
    setError("");
    const payload = { name: "New Org Unit", type: "Company", code: "", parent_id: null };
    const resp = await postData("t2t/org-units", payload);
    if (resp.error) {
      setError(resp.error.message || "Failed to create org unit.");
    } else {
      const newNode = resp.data?.data;
      await fetchOrgUnits();
      if (newNode) {
        setSelectedId(newNode.id);
        setEditForm({ name: newNode.name, type: newNode.type, code: newNode.code, parent_id: "" });
      }
    }
    setSaving(false);
  };

  // ---- Add Child ----
  const addChild = async () => {
    if (!selectedId) return;
    setSaving(true);
    setError("");
    const payload = { name: "New Unit", type: "Department", code: "", parent_id: selectedId };
    const resp = await postData("t2t/org-units", payload);
    if (resp.error) {
      setError(resp.error.message || "Failed to create child unit.");
    } else {
      const newNode = resp.data?.data;
      await fetchOrgUnits();
      if (newNode) {
        setExpandedIds((prev) => new Set([...prev, selectedId]));
        setSelectedId(newNode.id);
        setEditForm({ name: newNode.name, type: newNode.type, code: newNode.code, parent_id: selectedId });
      }
    }
    setSaving(false);
  };

  // ---- Delete Node ----
  const deleteNode = async () => {
    if (!selectedId) return;
    const childCount = nodes.filter((n) => n.parent_id === selectedId).length;
    const descendants = collectDescendants(nodes, selectedId);
    const msg = childCount > 0
      ? `This node has ${childCount} child(ren). All children (${descendants.length + 1} total) will also be deleted.`
      : "This org unit will be permanently deleted.";
    const title = childCount > 0 ? "Delete Org Unit & Children?" : "Delete Org Unit?";
    const ok = await confirm(msg, title);
    if (!ok) return;
    setSaving(true);
    setError("");
    const resp = await deleteData(`t2t/org-units/${selectedId}`);
    if (resp.error) {
      toast(resp.error.message || "Failed to delete.", "error");
      setError(resp.error.message || "Failed to delete.");
    } else {
      toast("Org unit deleted.", "success");
      setSelectedId(null);
      setEditForm({});
      await fetchOrgUnits();
    }
    setSaving(false);
  };

  // Parent options — exclude self and its descendants
  const excluded      = selectedId ? new Set([selectedId, ...collectDescendants(nodes, selectedId)]) : new Set();
  const parentOptions = nodes.filter((n) => !excluded.has(n.id));

  return (
    <>
      <T2TTopNav
        title="Org Setup"
        actions={
          <div className="t2t-btn-group">
            <button className="t2t-btn t2t-btn-outline t2t-btn-sm" onClick={addRoot} disabled={saving}>+ Add Root</button>
            <button className="t2t-btn t2t-btn-primary t2t-btn-sm" onClick={addChild} disabled={!selectedId || saving}>+ Add Child</button>
            <button className="t2t-btn t2t-btn-danger t2t-btn-sm" onClick={deleteNode} disabled={!selectedId || saving}>Delete Node</button>
          </div>
        }
      />
      <div className="t2t-content-fill">
        <div className="t2t-org-body" style={{ height: "calc(100vh - 52px)" }}>

          {/* Tree Panel */}
          <div className="t2t-org-tree-panel">
            <div className="t2t-org-tree-header">
              <h3 style={{ fontSize: 13, fontWeight: 700 }}>Organisation Hierarchy</h3>
            </div>
            <div className="t2t-org-tree-body">
              {loading && (
                <div style={{ padding: 20, textAlign: "center", fontSize: 12, color: "var(--t2t-text-muted)" }}>Loading...</div>
              )}
              {!loading && nodes.length === 0 && (
                <div style={{ padding: 20, textAlign: "center", fontSize: 12, color: "var(--t2t-text-muted)" }}>
                  No org units yet. Click <strong>+ Add Root</strong> to start.
                </div>
              )}
              {!loading && tree.map((node) => (
                <TreeNode
                  key={node.id}
                  node={node}
                  depth={0}
                  selectedId={selectedId}
                  expandedIds={expandedIds}
                  onSelect={handleSelect}
                  onToggle={toggleNode}
                />
              ))}
            </div>
          </div>

          {/* Props Panel */}
          <div className="t2t-org-props-panel">
            {error && (
              <div style={{ padding: "8px 12px", background: "#FEF2F2", color: "var(--t2t-danger)", borderRadius: 6, fontSize: 12, marginBottom: 16 }}>
                {error}
              </div>
            )}
            {!selectedNode ? (
              <div className="t2t-empty-state">
                <div className="t2t-empty-icon">🏢</div>
                <div className="t2t-empty-title">Select a node</div>
                <div className="t2t-empty-desc">Click any org unit in the tree to view and edit its properties.</div>
              </div>
            ) : (
              <div style={{ maxWidth: 480 }}>
                {/* Header */}
                <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 20 }}>
                  <span className="t2t-org-type-badge" style={{ background: TYPE_COLORS[selectedNode.type] || "#94A3B8", fontSize: 12, padding: "3px 8px" }}>
                    {selectedNode.type.toUpperCase()}
                  </span>
                  <div>
                    <div style={{ fontWeight: 700, fontSize: 15 }}>{selectedNode.name}</div>
                    <div style={{ fontSize: 11, color: "var(--t2t-text-muted)" }}>{selectedNode.code || "No code"}</div>
                  </div>
                </div>

                <div className="t2t-fg">
                  <label>Name</label>
                  <input className="t2t-fc" value={editForm.name || ""} onChange={(e) => handleFieldChange("name", e.target.value)} />
                </div>
                <div className="t2t-fg">
                  <label>Type</label>
                  <select className="t2t-fc" value={editForm.type || ""} onChange={(e) => handleFieldChange("type", e.target.value)}>
                    {TYPE_OPTIONS.map((t) => <option key={t} value={t}>{t}</option>)}
                  </select>
                </div>
                <div className="t2t-fg">
                  <label>Code</label>
                  <input className="t2t-fc" value={editForm.code || ""} placeholder="e.g. FIN, MFG" onChange={(e) => handleFieldChange("code", e.target.value)} />
                </div>
                <div className="t2t-fg">
                  <label>Parent Org Unit</label>
                  <select className="t2t-fc" value={editForm.parent_id ?? ""} onChange={(e) => handleFieldChange("parent_id", e.target.value)}>
                    <option value="">No parent (root)</option>
                    {parentOptions.map((n) => (
                      <option key={n.id} value={n.id}>{n.name}</option>
                    ))}
                  </select>
                </div>
                <div className="t2t-btn-group" style={{ marginTop: 20 }}>
                  <button className="t2t-btn t2t-btn-primary" onClick={handleSave} disabled={saving}>
                    {saving ? "Saving..." : "Save Changes"}
                  </button>
                  <button className="t2t-btn t2t-btn-ghost" disabled={saving} onClick={() => { setSelectedId(null); setEditForm({}); }}>
                    Cancel
                  </button>
                </div>
              </div>
            )}
          </div>

        </div>
      </div>
    </>
  );
};

export default T2TOrgSetup;
